// Background matte manager (§11.12, §8). Finds or computes the base matte of a photo:
//   auto: memory → IndexedDB → the folder's <name>.epb.mask.png → segmentation (counted, for the cache test);
//   key:  computed from the key settings (fast, in the worker), memory-cached.
// The matte cache key (hash of the source + model id) is stored in settings.background.maskCacheKey.
import * as Comlink from 'comlink';
import { create } from 'zustand';
import { createStore, delMany, get, set, type UseStore } from 'idb-keyval';
import { guard } from '../../io/workerGuard';
import { LRU_KEY, touchLru, type LruIndex } from './matteLru';
import type { SegmentWorkerApi } from './segment.worker';
import type { KeyParams, Matte, ModelSpec } from './matteMath';
import { decodePool } from '../../io/decode/pool';
import { decodePngToRgba16 } from '../../io/decode/png';
import { encodeGray8 } from '../../io/encode/png';
import { useLibrary, type LibraryPhoto } from '../../state/library';
import { useEditor } from '../../state/editor';
import type { Settings } from '../../model/types';

export const MATTE_EDGE = 2048;

type Status = 'idle' | 'loading' | 'running' | 'ready' | 'error';
export type MatteState = {
  /** Per photo id. */
  status: Record<string, { state: Status; message?: string }>;
  /** Bumped whenever a matte becomes available (viewer re-reads). */
  version: number;
  /** Models present in public/models (null until checked). */
  available: ModelSpec['id'][] | null;
  /** 'auto' = U²-Netp (meets the §11.12 speed targets on the owner's laptop); IS-Net is opt-in (sharper, slower). */
  model: 'auto' | ModelSpec['id'];
  lastRun: { model: string; ep: string; inferMs: number; totalMs: number } | null;
  batch: { done: number; total: number; cancel: boolean } | null;
  set: (p: Partial<Omit<MatteState, 'set'>>) => void;
};
export const useMatte = create<MatteState>((setS) => ({
  status: {},
  version: 0,
  available: null,
  model: 'auto',
  lastRun: null,
  batch: null,
  set: (p) => setS(p),
}));

/** Counters for tests (§11.12 acceptance 4: no second inference after reopening). */
export const matteStats = { inferences: 0, cacheHits: 0, fileHits: 0 };

let worker: Comlink.Remote<SegmentWorkerApi> | null = null;
let rawWorker: Worker | null = null;
export function segWorker(): Comlink.Remote<SegmentWorkerApi> {
  if (!worker) {
    rawWorker = new Worker(new URL('./segment.worker.ts', import.meta.url), {
      type: 'module',
      name: 'segment',
    });
    const raw = rawWorker;
    worker = guard(raw, Comlink.wrap<SegmentWorkerApi>(raw), () => {
      raw.terminate();
      if (rawWorker === raw) {
        rawWorker = null;
        worker = null;
      }
    });
  }
  return worker;
}

let db: UseStore | null = null;
const mattes = () => (db ??= createStore('epb-mattes', 'mattes'));
/** Stores a matte and keeps the store to the newest MATTE_CACHE_MAX entries (S15). */
async function storeMatte(key: string, m: Matte) {
  try {
    await set(key, m, mattes());
    await touchStored(key);
  } catch {
    /* storage unavailable: the in-memory and folder caches still work */
  }
}
async function touchStored(key: string) {
  const { index, evict } = touchLru((await get<LruIndex>(LRU_KEY, mattes())) ?? [], key, Date.now());
  await set(LRU_KEY, index, mattes());
  if (evict.length) await delMany(evict, mattes());
}
const memory = new Map<string, Matte>();
const remember = (k: string, m: Matte) => {
  memory.delete(k);
  memory.set(k, m);
  while (memory.size > 6) memory.delete(memory.keys().next().value as string);
};

function fnv(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}
const photoKey = (p: LibraryPhoto) => `${p.name}|${p.size}|${p.lastModified}`;
export const cacheKeyFor = (p: LibraryPhoto, model: string) => `${model}-${fnv(photoKey(p))}`;

const setStatus = (id: string, state: Status, message?: string) =>
  useMatte.setState((st) => ({ status: { ...st.status, [id]: { state, message } } }));

export async function checkModels(): Promise<ModelSpec['id'][]> {
  const cur = useMatte.getState().available;
  if (cur) return cur;
  const a = await segWorker()
    .available()
    .catch(() => [] as ModelSpec['id'][]);
  useMatte.getState().set({ available: a });
  return a;
}

export async function chosenModel(): Promise<ModelSpec['id']> {
  const st = useMatte.getState();
  const avail = await checkModels();
  if (st.model !== 'auto' && avail.includes(st.model)) return st.model;
  return avail.includes('u2netp') || !avail.includes('isnet') ? 'u2netp' : 'isnet';
}

async function ensureImage(p: LibraryPhoto) {
  const w = segWorker();
  const k = photoKey(p);
  if (await w.hasImage(k)) return;
  const small = await decodePool().small(p.file, MATTE_EDGE);
  await w.setImage(k, Comlink.transfer(small, [small.data.buffer as ArrayBuffer]));
}

function decodeMaskPng(bytes: Uint8Array): Matte | null {
  const d = decodePngToRgba16(bytes);
  if (!d) return null;
  const data = new Uint8Array(d.width * d.height);
  for (let i = 0; i < data.length; i++) data[i] = d.data[i * 4]! >> 8;
  return { width: d.width, height: d.height, data };
}

/** The auto-mode matte of a photo, from cache or by running the model (one inference per photo+model). */
const inflight = new Map<string, Promise<Matte>>();
export async function autoMatte(p: LibraryPhoto, opts: { force?: boolean } = {}): Promise<Matte> {
  const model = await chosenModel();
  const key = cacheKeyFor(p, model);
  const running = inflight.get(key);
  if (running) return running;
  const job = autoMatteNow(p, model, key, opts).finally(() => inflight.delete(key));
  inflight.set(key, job);
  return job;
}
async function autoMatteNow(
  p: LibraryPhoto,
  model: ModelSpec['id'],
  key: string,
  opts: { force?: boolean },
): Promise<Matte> {
  if (!opts.force) {
    const m = memory.get(key);
    if (m) return m;
    const stored = await get<Matte>(key, mattes()).catch(() => undefined);
    if (stored?.data) {
      void touchStored(key).catch(() => {});
      matteStats.cacheHits++;
      remember(key, stored);
      return stored;
    }
    const s = useEditor.getState().settings[p.id];
    const folder = useLibrary.getState().folder;
    if (folder && s?.background.maskCacheKey === key) {
      const blob = await folder.readMask(p).catch(() => null);
      const fromFile = blob && decodeMaskPng(new Uint8Array(await blob.arrayBuffer()));
      if (fromFile) {
        matteStats.fileHits++;
        remember(key, fromFile);
        await storeMatte(key, fromFile);
        return fromFile;
      }
    }
  }
  setStatus(p.id, 'loading');
  try {
    await ensureImage(p);
    setStatus(p.id, 'running');
    const r = await segWorker().segment(photoKey(p), model);
    matteStats.inferences++;
    useMatte
      .getState()
      .set({ lastRun: { model: r.model, ep: r.ep, inferMs: r.inferMs, totalMs: r.totalMs } });
    remember(key, r.matte);
    await storeMatte(key, r.matte);
    const folder = useLibrary.getState().folder;
    if (folder)
      await folder.writeMask(p, encodeGray8(r.matte.width, r.matte.height, r.matte.data)).catch(() => {});
    // Record which matte belongs to the settings (no undo step: this is a cache pointer, not an edit).
    const cur = useEditor.getState().settings[p.id];
    if (cur && cur.background.maskCacheKey !== key)
      useEditor.getState().replace(p.id, { ...cur, background: { ...cur.background, maskCacheKey: key } });
    setStatus(p.id, 'ready');
    return r.matte;
  } catch (e) {
    setStatus(p.id, 'error', (e as Error).message);
    throw e;
  }
}

/** Key-mode matte (non-AI), cached on the key settings. */
export async function keyMatteFor(p: LibraryPhoto, k: KeyParams): Promise<Matte> {
  const key = `key-${fnv(photoKey(p))}-${fnv(JSON.stringify(k))}`;
  const m = memory.get(key);
  if (m) return m;
  await ensureImage(p);
  const r = await segWorker().key(photoKey(p), k);
  remember(key, r);
  return r;
}

/** Synchronous lookup for the viewer: the matte if it is already in memory. */
export function matteInMemory(p: LibraryPhoto, s: Settings): { matte: Matte; key: string } | null {
  const b = s.background;
  if (b.mode === 'auto' && b.maskCacheKey) {
    const m = memory.get(b.maskCacheKey);
    return m ? { matte: m, key: b.maskCacheKey } : null;
  }
  if (b.mode === 'key' && b.key) {
    const key = `key-${fnv(photoKey(p))}-${fnv(JSON.stringify(b.key))}`;
    const m = memory.get(key);
    return m ? { matte: m, key } : null;
  }
  return null;
}

/** The matte the settings call for (computing it if needed); null when the background is not removed. */
export async function matteFor(p: LibraryPhoto, s: Settings): Promise<{ matte: Matte; key: string } | null> {
  const b = s.background;
  if (b.mode === 'auto') {
    const m = await autoMatte(p);
    const key = cacheKeyFor(p, await chosenModel());
    return { matte: m, key };
  }
  if (b.mode === 'key' && b.key) {
    const m = await keyMatteFor(p, b.key);
    return { matte: m, key: `key-${fnv(photoKey(p))}-${fnv(JSON.stringify(b.key))}` };
  }
  return null;
}

/**
 * "Remove background on N selected" (§11.12): sets auto mode on each photo and runs the queue one by one
 * with progress; Cancel stops after the current photo. Returns the number processed.
 */
export async function removeBackgroundBatch(
  ids: string[],
  opts: { setMode?: boolean } = {},
): Promise<{ done: number; failed: number }> {
  const lib = useLibrary.getState();
  if (opts.setMode !== false) useEditor.getState().updateMany(ids, (s) => void (s.background.mode = 'auto'));
  useMatte.getState().set({ batch: { done: 0, total: ids.length, cancel: false } });
  let done = 0,
    failed = 0;
  for (const id of ids) {
    if (useMatte.getState().batch?.cancel) break;
    const p = lib.photos.find((x) => x.id === id);
    if (!p) continue;
    try {
      await autoMatte(p);
    } catch {
      failed++;
    }
    done++;
    useMatte.setState((st) => ({ batch: st.batch && { ...st.batch, done }, version: st.version + 1 }));
  }
  useMatte.getState().set({ batch: null });
  return { done, failed };
}

/** Test hook passthrough. */
export const setSegmentTestOptions = (o: { failLoad?: boolean; preferGpu?: boolean }) => {
  useMatte.getState().set({ available: null });
  return segWorker().setTestOptions(o);
};
